import {
  Button,
  DialogActions,
  DialogContent,
  FormControl,
  TextField,
} from "@mui/material";
import PropTypes from "prop-types";
import { Controller, useForm } from "react-hook-form";
import FormError from "../../common/components/FormError";
import { toast } from "react-toastify";
import TOASTER from "../../config/toaster";
import { COMMON_MSG } from "../../common/messages";
import logger from "../../helpers/logger";
import DraggableDialog from "../../common/components/DraggableDialog";
import { addTimeFrameData, updateTimeFrameData } from "../../api/timesetup-api-service";
import React, { useState } from "react";
import { IMaskInput } from "react-imask";

const TimeMaskInput = React.forwardRef(function TimeMaskInput(props, ref) {
  const { onChange, ...other } = props;

  return (
    <IMaskInput
      {...other}
      mask="00:00:00"
      inputRef={ref}
      onAccept={(value) => onChange({ target: { value } })}
      placeholder="HH:MM:SS"
      lazy={false}
    />
  );
});

// // Custom input component for description masking (1 or 2 digits, dash, 1 or 2 digits)
const DescriptionMaskInput = React.forwardRef(function DescriptionMaskInput(props, ref) {
  const { onChange, onBlur, name, ...other } = props;

  return (
    <IMaskInput
      {...other}
      mask="0[0]-0[0]"
      definitions={{
        '0': /[0-9]/,
      }}
      lazy={false}
      inputRef={ref}
      name={name}
      onAccept={(value) => {
        if (onChange) {
          // Remove bracket placeholders and create clean value
          const cleanValue = value.replace(/[\[\]]/g, '');
          onChange({ target: { name, value: cleanValue } });
        }
      }}
      onBlur={(e) => {
        if (onBlur) {
          // Trigger validation on blur
          onBlur(e);
        }
      }}
      placeholder="1-99 or 01-99"
    />
  );
});

const TimeFrameDialog = (props) => {
  const {
    handleSubmit,
    control,
    formState: { errors, isValid, isSubmitting, isDirty },
  } = useForm({
    defaultValues: props.formData,
    mode: "all",
  });

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const handleCancel = () => {
    if (isDirty) {
      setHasUnsavedChanges(true);
    } else {
      props.onClose();
    }
  };

  const handleCrudOperation = async (data) => {
    let toastId = toast.loading(COMMON_MSG.Processing);

    try {
      let response;

      if (data?.timeFrame_Key) {
        response = await updateTimeFrameData(
          data?.timeFrame_Key,
          data,
          toastId
        );
      } else {
        response = await addTimeFrameData(data, toastId);
      }

      if (response.isSuccess) {
        await props.onSuccess();
        toast.update(toastId, {
          render: response.message,
          type: "success",
          isLoading: false,
          autoClose: TOASTER.AUTO_CLOSE,
        });
      }

      props.onClose();
    } catch (error) {
      logger.log(error);
    }
  };

  return (
    <DraggableDialog
      onClose={props.onClose}
      title={props?.isEditing ? "Edit Time Frame" : "Add Time Frame"}
      maxWidth="sm"
      fullWidth
      open={props.open}
      hasUnsavedChanges={isDirty}
      showUnsavedConfirmCancel={hasUnsavedChanges}
      onUnsavedConfirmCancel={() => setHasUnsavedChanges(false)}
    >
      <DialogContent>
        {/* TimeFrame Description */}
        <FormControl fullWidth margin="normal">
          <Controller
            name="timeFrame_Desc"
            control={control}
            rules={{
              required: "Time Frame Description is required",
              pattern: {
                value: /^(\d{1,2})-(\d{1,2})$/,
                message: "Description must be in format: #-# or ##-##",
              },
              validate: {
                complete: (value) => {
                  if (!value || value.length < 3)
                    return "Please enter complete description";
                  if (!value.includes("-"))
                    return "Description must contain a dash (-)";
                  const parts = value.split("-");
                  if (parts.length !== 2 || !parts[0] || !parts[1])
                    return "Please enter numbers on both sides of the dash";
                  return true;
                },
              },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Description"
                fullWidth
                required
                margin="normal"
                error={!!errors.timeFrame_Desc}
                InputProps={{ inputComponent: DescriptionMaskInput }}
              />
            )}
          />
          <FormError errors={errors} controlId="timeFrame_Desc" />
        </FormControl>

        {/* TimeFrame Time */}
        <FormControl fullWidth margin="normal">
          <Controller
            name="timeFrame_Time"
            control={control}
            rules={{
              required: "Time Frame Time is required",
              pattern: {
                value: /^([01]\d|2[0-3]):([0-5]\d):([0-5]\d)$/,
                message:
                  "Time must be in HH:MM:SS format (00:00:00 to 23:59:59)",
              },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Time"
                fullWidth
                required
                margin="normal"
                error={!!errors.timeFrame_Time}
                InputProps={{ inputComponent: TimeMaskInput }}
              />
            )}
          />
          <FormError errors={errors} controlId="timeFrame_Time" />
        </FormControl>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleCancel} sx={{ color: "gray" }}>
          Cancel
        </Button>
        <Button
          sx={{ marginRight: "15px" }}
          variant="contained"
          color="primary"
          onClick={handleSubmit(handleCrudOperation)}
          disabled={!isValid || isSubmitting}
        >
          Save
        </Button>
      </DialogActions>
    </DraggableDialog>
  );
};

TimeFrameDialog.propTypes = {
  open: PropTypes.bool,
  formData: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  isEditing: PropTypes.bool.isRequired,
};

export default TimeFrameDialog;
